<template>
    <div class="mt-3 mb-3">
        <div class="row">
            <!-- Left panel: Video player + controls -->
            <div class="col-12 col-lg-6 mb-3 mb-lg-0 pe-lg-2">
                <div class="card p-4 h-100 d-flex flex-column">
                    <!-- Header -->
                    <div class="d-flex align-items-center mb-1">
                        <span
                            class="trip-color-dot me-2"
                            :style="{ backgroundColor: trip.color || '#7FC000' }"
                        />
                        <font-awesome-icon
                            icon="video"
                            class="me-2 text-muted"
                        />
                        <span class="fw-bold">{{ trip.task }} — {{ formatDateShort(trip.start_time) }}</span>
                    </div>

                    <div class="d-flex flex-row gap-1 mt-1">
                        <div class="small fw-semibold">
                            {{ trip.license_plate }} -
                        </div>

                        <div class="small">
                            {{ trip.driver_name }} -
                        </div>
                        <div class="small">
                            {{ trip.contract }} -
                        </div>
                        <div class="small mb-3">
                            {{ formatDistance(trip.length) }} km
                        </div>
                    </div>

                    <!-- Video player -->
                    <video-player
                        ref="videoPlayer"
                        :segments="videoSegments"
                        :loading="videoSegmentsLoading"
                        @time-update="onTimeUpdate"
                        @create-observation="onCreateObservation"
                    />
                </div>
            </div>

            <!-- Right panel: Map + observations -->
            <div class="col-12 col-lg-6 mb-3 mb-lg-0 ps-lg-2">
                <div class="card p-4 h-100 d-flex flex-column">
                    <!-- Header -->
                    <div class="d-flex align-items-center justify-content-between mb-1">
                        <div>
                            <font-awesome-icon
                                icon="route"
                                class="me-2"
                            />
                            <span class="fw-bold">{{ $t('video_recordings.route_on_map') }}</span>
                        </div>

                        <div class="text-muted small">
                            <font-awesome-icon
                                icon="road"
                                class="me-1"
                            />
                            {{ formatDistance(trip.length) }} km
                        </div>
                    </div>

                    <div class="d-flex flex-row justify-content-between mt-2">
                        <div class="text-muted small mb-3">
                            {{ $t('video_recordings.map_click_hint') }}
                        </div>

                        <div class="dropdown">
                            <button
                                class="btn btn-sm btn-outline-secondary dropdown-toggle"
                                type="button"
                                data-bs-toggle="dropdown"
                            >
                                {{ $t('video_recordings.show_observations') }}
                            </button>
                            <ul class="dropdown-menu dropdown-menu-end">
                                <li>
                                    <button
                                        class="dropdown-item"
                                        :class="{ active: !showAllObservations }"
                                        type="button"
                                        @click="selectOption(false)"
                                    >
                                        {{ $t('video_recordings.observations_reported_during_trip') }}
                                    </button>
                                </li>
                                <li>
                                    <button
                                        class="dropdown-item"
                                        :class="{ active: showAllObservations }"
                                        type="button"
                                        @click="selectOption(true)"
                                    >
                                        {{ $t('video_recordings.observations_open_during_trip') }}
                                    </button>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <!-- Map -->
                    <div class="detail-map-wrapper border rounded overflow-hidden">
                        <map-container
                            ref="detailMapContainer"
                            :find-user="false"
                            :menu-enabled="false"
                            @on-map-changed="onMapReady"
                            @on-marker-tap="onObservationMarkerTap"
                        />
                    </div>
                </div>
            </div>
        </div>

        <!-- Create observation from the current video frame -->
        <observation-wizard
            v-if="showObservationWizard"
            :coordinates="observationCoordinates"
            :contract="trip.contract_id"
            :initial-photos="observationPhotos"
            :show-thumbnail="true"
            :created-time="observationCreatedTime"
            @close="closeObservationWizard"
            @close-and-update="onWizardSaved"
        />
    </div>
</template>

<script>
/**
 * VideoRecordingsDetail
 * =====================
 * Shows the dashcam video for a trip alongside a map, and keeps a marker on
 * the map synced to the video's playback position (and vice versa).
 *
 * THE CORE PROBLEM: aligning two independent clocks
 * -------------------------------------------------
 * There are two sources of timing that must be lined up:
 *
 *   1. The ROUTE  — `tripDetail.geometry` (lat/lng points) with a parallel
 *      `tripDetail.timestamps` array. These timestamps are genuine UTC and
 *      tell us where the vehicle was at each moment.
 *
 *   2. The VIDEO  — a set of dashcam segments. Each segment has a `recordedAt`
 *      wall-clock time. The video player reports its position as "seconds
 *      elapsed since the first segment started recording".
 *
 * We cannot compare the two clocks in absolute terms because the segment
 * `recordedAt` values are mislabelled by a whole-hour timezone error (the
 * backend tags Finnish local time as "+00:00"). Observed example: route
 * starts 07:56:26 UTC, first segment `recordedAt` says 10:56:31 — exactly
 * 3h 00m 05s apart. The 3h is the timezone bug; the leftover 5s is the real
 * delay between the trip starting and the dashcam beginning to record.
 *
 * THE SOLUTION: align by elapsed time from each clock's own start
 * --------------------------------------------------------------
 * `computeStartDelay()` measures the gap between the video's first
 * `recordedAt` and the route's first timestamp, strips the whole-hour
 * timezone error, and keeps the sub-hour remainder as `startDelaySec` (the
 * genuine "video started N seconds into the trip" delay). Then:
 *
 *   routeElapsed = videoElapsed + startDelaySec
 *
 * maps a video position onto the route, and the inverse maps a clicked route
 * point back to a video position. This is timezone-proof: if the backend is
 * ever fixed to emit true UTC, the whole-hour part rounds to 0 and the same
 * code still yields the correct 5s delay.
 *
 * SMOOTH MOVEMENT: a self-driving animation clock
 * -----------------------------------------------
 * The <video> element only fires `timeupdate` ~4x/second, which is too coarse
 * to move a marker smoothly (it looks like it's jumping in bursts). Instead we
 * run our own requestAnimationFrame loop (~60fps) that advances `playbackElapsed`
 * by real wall-clock time each frame. The video's `timeupdate` events are used
 * only to gently CORRECT that clock toward the true position, not to drive it
 * frame-by-frame. See `animationStep()` for details.
 */
import L from 'leaflet';
import MapContainer from '../map/MapContainer.vue';
import VideoPlayer from './VideoPlayer.vue';
import ObservationWizard from '../observation/ObservationWizard.vue';
import { mapHelper } from '../mixins/MapMixin';
import { catmullRomPosition } from './catmullRom';
import { inject } from 'vue';
import obsSvg from '@/assets/observation.svg';
import obsClosedSvg from '@/assets/observation_closed.svg';


export default {
    name: 'VideoRecordingsDetail',
    components: {
        MapContainer,
        VideoPlayer,
        ObservationWizard
    },
    mixins: [mapHelper],
    props: {
        trip: {
            type: Object,
            required: true
        },
        tripDetail: {
            type: Object,
            default: null
        },
        videoSegments: {
            type: Array,
            default: () => []
        },
        videoSegmentsLoading: {
            type: Boolean,
            default: false
        },
        observations: {
            type: Array,
            default: () => []
        },
        showAllObservations: {
            type: Boolean,
            default: false
        }
    },
    emits: ['marker-selected', 'observation-created', 'toggle-show-all-observations'],
    setup() {
        // Inject parents alerter
        const showAlert = inject('showAlert');
        return { showAlert };
    },
    data() {
        return {
            // Minimum zoom level to focus on when centering the map on an
            // observation. If the map is already zoomed in at least this far
            // the current zoom is kept; otherwise it zooms in to this level.
            // Adjustable here in one place.
            FOCUS_MIN_ZOOM: 15,
            map: null,
            currentVideoTime: 0,
            positionMarker: null,
            // Current marker lat/lng (where the vehicle was at the frame the
            // user is looking at). Captured when opening the observation flow.
            currentMarkerLat: null,
            currentMarkerLng: null,
            // Observation-from-frame wizard state
            showObservationWizard: false,
            observationCoordinates: [],
            observationPhotos: [],
            observationCreatedTime: null,
            // Self-driving animation clock (seconds elapsed since route start).
            playbackElapsed: 0,      // our current best estimate of position in time
            targetElapsed: 0,        // last value reported/corrected by the video
            isPlaying: false,        // whether to keep advancing the clock
            lastFrameTime: 0,        // performance.now() of previous animation frame
            animationFrameId: null,
            timestampsMs: [],        // cached route timestamps in ms
            routeDurationSec: 0,     // total route span in seconds
            // Seconds to add to the video's elapsed time to line it up with
            // the route. Accounts for the dashcam starting a few seconds after
            // the trip, while ignoring the whole-hour timezone mislabelling of
            // recordedAt. Recomputed whenever the segments/route change.
            startDelaySec: 0
        }
    },
    watch: {
        trip() {
            this.stopAnimation()
            this.removePositionMarker()
            this.playbackElapsed = 0
            this.targetElapsed = 0
            if (this.map) {
                this.map.removePolylinesByType(this.TRACE)
                this.map.removeMapItemsByType(this.OBSERVATION)
                this.map.resizeMap()
                this.$nextTick(() => {
                    this.drawRoute()
                    this.drawObservations()
                })
            }
        },
        tripDetail() {
            this.cacheRouteTimestamps()
            if (this.map && this.tripDetail) {
                this.map.removePolylinesByType(this.TRACE)
                this.drawRoute()
            }
        },
        videoSegments() {
            // Segments may arrive after tripDetail (separate request), so
            // recompute the start-delay offset once they're available.
            this.computeStartDelay()
        },
        observations() {
            // Observations arrive from a separate request; (re)draw them when
            // they change. Only relevant once the map exists.
            if (this.map) {
                this.drawObservations()
            }
        }
    },
    mounted() {
        this.cacheRouteTimestamps()
        document.addEventListener('keydown', this.onKeyDown)
    },
    beforeUnmount() {
        this.stopAnimation()
        this.removePositionMarker()
        if (this.map && this.map.map) {
            this.map.map.off('click', this.onMapClick)
        }
        document.removeEventListener('keydown', this.onKeyDown)
    },
    methods: {
        // Pause already happened in the player. Capture the current frame,
        // snapshot the marker's current coordinate, and open the wizard with
        // the frame pre-attached as a photo.
        async onCreateObservation() {
            const player = this.$refs.videoPlayer
            const frame = player ? await player.captureFrame() : null

            if (!frame) {
                // Tainted canvas (missing CORS headers) or no frame ready.
                this.showAlert(this.$t('video_recordings.capture_failed'))
                return
            }

            // Location = where the vehicle was at this frame. Fall back to the
            // route's first point if the marker hasn't been placed yet.
            let lat = this.currentMarkerLat
            let lng = this.currentMarkerLng
            if ((lat === null || lng === null) &&
                this.tripDetail && this.tripDetail.geometry && this.tripDetail.geometry.length) {
                lat = this.tripDetail.geometry[0].y
                lng = this.tripDetail.geometry[0].x
            }

            if (lat === null || lng === null) {
                this.showAlert(this.$t('video_recordings.capture_failed'))
                return
            }

            this.observationCoordinates = [{ lat, lng }]
            this.observationPhotos = [frame]

            // Compute the real-world timestamp of the current video frame.
            // playbackElapsed = seconds since route start; timestampsMs[0] =
            // absolute route start in ms. The observation should be stamped
            // with the time the vehicle was at this position, not "now."
            // NOTE: sent as a NAIVE LOCAL wall-clock string (no timezone/Z),
            // to match the backend's naive DATETIME column. Trying this format
            // first to see how it lands in the DB.
            if (this.timestampsMs.length > 0) {
                const d = new Date(this.timestampsMs[0] + (this.playbackElapsed * 1000))
                const year = d.getFullYear()
                const month = String(d.getMonth() + 1).padStart(2, '0')
                const day = String(d.getDate()).padStart(2, '0')
                const hours = String(d.getHours()).padStart(2, '0')
                const minutes = String(d.getMinutes()).padStart(2, '0')
                const seconds = String(d.getSeconds()).padStart(2, '0')
                this.observationCreatedTime = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`
            } else {
                this.observationCreatedTime = null
            }

            this.showObservationWizard = true
        },

        // Arrow-key seeking. Left/Right = ±1s, Shift+Left/Right = ±5s. Ignored
        // while typing in a field or while the observation wizard is open, so it
        // never fights text entry or modal interaction. preventDefault stops the
        // key from also panning the Leaflet map / scrolling the page.
        onKeyDown(event) {
            if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight' && event.code !== 'Space') return
            if (this.showObservationWizard) return
            if (this.isTypingTarget(event.target)) return

            const player = this.$refs.videoPlayer
            if (!player) return

            event.preventDefault()

            const step = event.shiftKey ? 5 : 1

            if (event.key === 'ArrowLeft') {
                player.seekBy(-step)
            }

            if (event.key === 'ArrowRight') {
                player.seekBy(step)
            }

            if (event.code === 'Space') {
                player.isPlaying ? player.pause() : player.play()
            }
        },

        // True when focus is in an element that should receive arrow keys itself.
        isTypingTarget(target) {
            if (!target) return false
            const tag = target.tagName
            return tag === 'INPUT' ||
                tag === 'TEXTAREA' ||
                tag === 'SELECT' ||
                target.isContentEditable === true
        },

        closeObservationWizard() {
            this.showObservationWizard = false
            this.observationCoordinates = []
            this.observationPhotos = []
            this.observationCreatedTime = null
        },

        // Wizard reported a successful create/update: close it and ask the
        // parent (which owns the observation data + selected trip) to refetch.
        onWizardSaved() {
            this.closeObservationWizard()
            this.$emit('observation-created')
        },

        // Observation display-mode dropdown. The parent owns the actual
        // showAllObservations state and refetches on change; we just forward
        // the chosen mode (false = reported during trip, true = open during trip).
        selectOption(value) {
            this.$emit('toggle-show-all-observations', value)
        },

        onMapReady(map) {
            this.map = map
            this.map.removePolylinesByType(this.TRACE)
            this.drawRoute()
            this.drawObservations()
            // Attach click handler for seeking video by map click
            if (this.map.map) {
                this.map.map.on('click', this.onMapClick)
            }
        },

        // Map marker tap → same sync as the table-row crosshairs: jump the
        // video + marker to that observation's spot on the route.
        onObservationMarkerTap(data) {
            if (!data || data.type !== this.OBSERVATION) return
            const observation = this.observations.find(o => o.id === data.id)
            if (observation) {
                this.seekToObservation(observation)
                // Tell the parent which observation was tapped so the table can
                // highlight the matching row (same result as the crosshairs).
                this.$emit('marker-selected', observation.id)
            }
        },

        // Draw the trip's observations as point markers on the map. Point-type
        // observations are the only kind these dashcam trips produce. Open uses
        // the standard observation icon, closed uses the greyed-out variant.
        // No auto-zoom: markers are drawn within the existing route framing.
        drawObservations() {
            if (!this.map) return
            this.map.removeMapItemsByType(this.OBSERVATION)
            if (!this.observations || !this.observations.length) return

            const markerStore = this.map.getMarkerStore()
            const openIcon = markerStore.getSvgMarker(obsSvg, null, null, '2em', '2em')
            const closedIcon = markerStore.getSvgMarker(obsClosedSvg, null, null, '2em', '2em')

            for (const observation of this.observations) {
                const point = observation.geometry && observation.geometry.point
                if (!point) continue
                const icon = observation.closed_time ? closedIcon : openIcon
                this.map.showMapMarker(observation.id, this.OBSERVATION, point.y, point.x, icon, false, 0)
            }
        },

        drawRoute() {
            if (!this.map) return

            // Use tripDetail geometry (with timestamps) if available, otherwise fall back to trip geometry
            let points = null
            if (this.tripDetail && this.tripDetail.geometry && this.tripDetail.geometry.length > 1) {
                // tripDetail.geometry is already [{x, y}] format
                points = this.tripDetail.geometry
            } else if (this.trip.geometry && this.trip.geometry.points && this.trip.geometry.points.length > 1) {
                points = this.trip.geometry.points.map(p => ({ x: p[0], y: p[1] }))
            }

            if (!points) return

            this.map.drawPolyLine(this.trip.id, this.TRACE, points, '#7FC000', false, false, 5)

            this.$nextTick(() => {
                if (this.map) {
                    this.map.zoomToGroup(this.TRACE, 18)
                }
            })
        },

        cacheRouteTimestamps() {
            if (this.tripDetail && this.tripDetail.timestamps && this.tripDetail.timestamps.length > 1) {
                this.timestampsMs = this.tripDetail.timestamps.map(t => new Date(t).getTime())
                this.routeDurationSec = (this.timestampsMs[this.timestampsMs.length - 1] - this.timestampsMs[0]) / 1000
            } else {
                this.timestampsMs = []
                this.routeDurationSec = 0
            }
            this.computeStartDelay()
        },

        // Figure out how many seconds after the route began the dashcam
        // started recording. recordedAt is mislabelled with a whole-hour
        // timezone error (e.g. Finnish local time tagged as +00:00), so we
        // strip the whole-hour part and keep only the sub-hour remainder as
        // the genuine start delay.
        computeStartDelay() {
            this.startDelaySec = 0
            if (!this.timestampsMs.length) return
            if (!this.videoSegments || !this.videoSegments.length) return

            const firstSeg = [...this.videoSegments].sort((a, b) => a.segmentIndex - b.segmentIndex)[0]
            if (!firstSeg || !firstSeg.recordedAt) return

            const videoStartMs = new Date(firstSeg.recordedAt).getTime()
            const routeStartMs = this.timestampsMs[0]
            const rawDiffSec = (videoStartMs - routeStartMs) / 1000

            // Remove the whole-hour timezone offset; keep the remainder.
            const timezoneErrorSec = Math.round(rawDiffSec / 3600) * 3600
            this.startDelaySec = rawDiffSec - timezoneErrorSec
        },

        // Called on every video timeupdate. We don't move the marker directly
        // here — instead we correct our self-driving clock toward the video's
        // reported time. The animation loop does the actual smooth movement.
        onTimeUpdate(globalTime, elapsedSec) {
            this.currentVideoTime = globalTime
            if (elapsedSec === null || elapsedSec === undefined) return

            // Shift the video's elapsed time onto the route's timeline by the
            // start delay (dashcam started a few seconds after the trip).
            const routeElapsed = elapsedSec + this.startDelaySec
            this.targetElapsed = routeElapsed

            // If our estimate has drifted far from the video (seek, stall,
            // first update), snap; otherwise let the loop ease toward it.
            if (Math.abs(this.playbackElapsed - routeElapsed) > 1.5) {
                this.playbackElapsed = routeElapsed
            }

            this.ensureAnimating()
        },

        // Starts the animation loop if it isn't already running. Safe to call
        // repeatedly — it no-ops when a frame is already scheduled.
        ensureAnimating() {
            if (this.animationFrameId !== null) return
            this.lastFrameTime = performance.now()
            this.animationFrameId = requestAnimationFrame(this.animationStep)
        },

        animationStep() {
            this.animationFrameId = null

            const now = performance.now()
            const deltaSec = (now - this.lastFrameTime) / 1000
            this.lastFrameTime = now

            const player = this.$refs.videoPlayer
            const playing = !!(player && player.isPlaying)
            const rate = player && player.playbackRate ? player.playbackRate : 1

            if (playing) {
                // Advance our own clock by real elapsed time (scaled by speed).
                this.playbackElapsed += deltaSec * rate

                // Gently pull toward the video's last reported time to correct
                // small drift without visible jumps (~10% of the gap per frame).
                const drift = this.targetElapsed - this.playbackElapsed
                this.playbackElapsed += drift * 0.1
            } else {
                // Paused: sit exactly where the video says.
                this.playbackElapsed = this.targetElapsed
            }

            this.renderMarkerAtElapsed(this.playbackElapsed)

            // Keep looping while playing; take one more frame when paused so
            // the marker settles, then stop to save CPU.
            if (playing) {
                this.animationFrameId = requestAnimationFrame(this.animationStep)
            }
        },

        stopAnimation() {
            if (this.animationFrameId !== null) {
                cancelAnimationFrame(this.animationFrameId)
                this.animationFrameId = null
            }
        },

        // Place the marker at the interpolated route position for a given
        // number of seconds elapsed since the route (and video) began.
        renderMarkerAtElapsed(elapsedSec) {
            if (!this.map || !this.map.map) return
            const timestampsMs = this.timestampsMs
            const geometry = this.tripDetail && this.tripDetail.geometry
            if (!geometry || geometry.length < 2 || timestampsMs.length < 2) return

            // Convert elapsed-seconds into an absolute time on the route's
            // clock (route timestamps are absolute ms).
            const currentMs = timestampsMs[0] + (elapsedSec * 1000)

            // Before the route starts or after it ends: pin to the endpoints.
            if (currentMs <= timestampsMs[0]) {
                this.setMarkerPosition(geometry[0].y, geometry[0].x)
                return
            }
            if (currentMs >= timestampsMs[timestampsMs.length - 1]) {
                const last = geometry[geometry.length - 1]
                this.setMarkerPosition(last.y, last.x)
                return
            }

            // Binary search for the pair of consecutive route points whose
            // timestamps bracket currentMs (i.e. timestampsMs[i] <= currentMs
            // < timestampsMs[i+1]). O(log n) since the trip can have thousands
            // of points and this runs every animation frame.
            let searchLow = 0
            let searchHigh = timestampsMs.length - 2
            while (searchLow <= searchHigh) {
                const searchMid = Math.floor((searchLow + searchHigh) / 2)
                if (currentMs < timestampsMs[searchMid]) {
                    searchHigh = searchMid - 1
                } else if (currentMs >= timestampsMs[searchMid + 1]) {
                    searchLow = searchMid + 1
                } else {
                    searchLow = searchMid
                    break
                }
            }

            // The fraction 0..1 of the way from point[pointIndex] to the next
            // point, based on how far currentMs is between their timestamps.
            // Guard against identical timestamps (vehicle stationary — avoids
            // divide-by-zero).
            const pointIndex = searchLow
            const timeBetweenPoints = timestampsMs[pointIndex + 1] - timestampsMs[pointIndex]
            const progressBetweenPoints = timeBetweenPoints > 0
                ? (currentMs - timestampsMs[pointIndex]) / timeBetweenPoints
                : 0

            // Follow a Catmull-Rom curve through the surrounding points rather
            // than a straight line, so the marker glides smoothly around the
            // sparse GPS points instead of kinking at each one. See catmullRom.js.
            const pos = catmullRomPosition(geometry, pointIndex, progressBetweenPoints)

            this.setMarkerPosition(pos.lat, pos.lng)
        },

        setMarkerPosition(lat, lng) {
            this.currentMarkerLat = lat
            this.currentMarkerLng = lng
            const rawMap = this.map.map
            if (!rawMap) return

            if (!this.positionMarker) {
                this.positionMarker = L.circleMarker([lat, lng], {
                    radius: 8,
                    color: '#ffffff',
                    weight: 2,
                    fillColor: '#7FC000',
                    fillOpacity: 1,
                    pane: 'markerPane'
                }).addTo(rawMap)
            } else {
                this.positionMarker.setLatLng([lat, lng])
            }
        },

        removePositionMarker() {
            if (this.positionMarker) {
                if (this.map && this.map.map) {
                    this.map.map.removeLayer(this.positionMarker)
                }
                this.positionMarker = null
            }
        },

        // Map click -> video seek. The inverse of the playback->marker sync:
        // find where on the route the user clicked, then jump the video to the
        // frame recorded at that moment.
        onMapClick(e) {
            this.seekToCoordinate(e.latlng.lat, e.latlng.lng)
        },

        // Core seek-by-position: find the route point nearest the given
        // coordinate, snap the playback clock + map marker there, and seek the
        // video to the matching frame. Shared by map clicks and table-row
        // observation clicks so all three sync entry points behave identically.
        // Seeking by position (not by observation timestamp) avoids the
        // observation created_time timezone ambiguity — the route timestamps
        // are the single source of truth for time.
        seekToCoordinate(lat, lng) {
            if (!this.tripDetail || !this.tripDetail.timestamps || !this.tripDetail.geometry) return

            const geometry = this.tripDetail.geometry
            const timestamps = this.tripDetail.timestamps
            if (geometry.length < 2 || timestamps.length < 2) return

            // Find the route point closest to the coordinate. Uses squared
            // distance (no sqrt needed) since we only compare magnitudes.
            let minDist = Infinity
            let nearestIndex = 0
            for (let i = 0; i < geometry.length; i++) {
                const dLat = geometry[i].y - lat
                const dLng = geometry[i].x - lng
                const dist = dLat * dLat + dLng * dLng
                if (dist < minDist) {
                    minDist = dist
                    nearestIndex = i
                }
            }

            // How far into the trip (seconds) that point is.
            const routeStartMs = new Date(timestamps[0]).getTime()
            const pointMs = new Date(timestamps[nearestIndex]).getTime()
            const routeElapsedSec = Math.max(0, (pointMs - routeStartMs) / 1000)

            // Snap our own clock and the marker straight to the point (no
            // gliding), so the marker responds instantly.
            this.playbackElapsed = routeElapsedSec
            this.targetElapsed = routeElapsedSec
            this.renderMarkerAtElapsed(routeElapsedSec)

            // Translate route time back to the video's own timeline (the video
            // starts startDelaySec after the route) and seek there.
            const videoElapsedSec = Math.max(0, routeElapsedSec - this.startDelaySec)
            if (this.$refs.videoPlayer) {
                this.$refs.videoPlayer.seekToElapsed(videoElapsedSec)
            }

            // Focus the map on the observation/coordinate so it is visible even
            // if it was off-screen. Shared by crosshairs click and marker tap.
            this.centerMapOn(lat, lng)
        },

        // Pan the map to the given coordinate. Keep the current zoom if the map
        // is already zoomed in at least FOCUS_MIN_ZOOM; otherwise zoom in to
        // FOCUS_MIN_ZOOM so the point is clearly visible.
        centerMapOn(lat, lng) {
            if (!this.map || lat === null || lat === undefined || lng === null || lng === undefined) return
            const currentZoom = this.map.getMapZoomLevel()
            const targetZoom = Math.max(currentZoom, this.FOCUS_MIN_ZOOM)
            this.map.zoomToPosition(lat, lng, targetZoom)
        },

        // Table-row observation click: jump the video + map marker to the
        // observation's location on the route. Point observations only.
        seekToObservation(observation) {
            if (!observation || !observation.geometry || !observation.geometry.point) return
            const point = observation.geometry.point
            this.seekToCoordinate(point.y, point.x)
        },

        formatDateShort(dateString) {
            if (!dateString) return '-'
            const date = new Date(dateString)
            if (isNaN(date.getTime())) return '-'
            const day = String(date.getDate()).padStart(2, '0')
            const month = String(date.getMonth() + 1).padStart(2, '0')
            const year = date.getFullYear()
            return `${day}.${month}.${year}`
        },

        formatDistance(km) {
            if (km === null || km === undefined) return '0'
            return Number(km).toFixed(1)
        }
    }
}
</script>

<style scoped>
.trip-color-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    display: inline-block;
}

.detail-map-wrapper {
    position: relative;
    width: 100%;
    height: 100%;
    aspect-ratio: 16 / 9;
}
</style>
